import { getAuthToken } from "@/api/vercel";
import { useQuery } from "@tanstack/react-query";

/**
 * Source of race-results + tips for the Performance pages.
 *
 * Calls the read-only `/api/race-results` reader, which joins the full-field
 * results (`lexi-puntingform-results-reader`) with our archived tips
 * (`results-archive`) on horseId and returns the race shape the pages consume
 * (see src/data/performance-mock.js). Swapping data source touches only this
 * hook — the pages, filters and metrics engine are unaffected.
 */
const DEFAULT_DAYS = 30;

async function fetchRacePerformance({ days, from, to, mode, track } = {}) {
  const headers = { "Content-Type": "application/json" };
  const token = getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const body = from && to ? { from, to } : { days: days ?? DEFAULT_DAYS };
  if (mode) body.mode = mode;
  if (track && track !== "all") body.track = track;
  const res = await fetch("/api/race-results", {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`race-results ${res.status} ${text}`.trim());
  }
  const json = await res.json();
  return json.races || [];
}

export function useRacePerformance(options = {}) {
  const { days, from, to, mode = "summary", track, ...rest } = options;
  return useQuery({
    queryKey: [
      "race-performance",
      mode,
      track || "all",
      from && to ? `${from}:${to}` : days ?? DEFAULT_DAYS,
    ],
    queryFn: () => fetchRacePerformance({ days, from, to, mode, track }),
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    ...rest,
  });
}
